// @ts-check
/**
 * Centralized API Client for Admin Frontend
 *
 * - Attaches admin auth token to every request
 * - Handles 401 by clearing auth state and redirecting to login
 * - Extracts error detail from JSON responses
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getAuthHeaders() {
  const token = localStorage.getItem('adminToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse(response) {
  if (response.ok) {
    if (response.status === 204) return null;
    return response.json();
  }

  // On 401, clear auth and redirect
  if (response.status === 401) {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    window.location.href = '/login';
    return;
  }

  // Extract error detail from response body
  const error = await response.json().catch(() => ({ detail: 'Something went wrong' }));
  throw buildApiError(response.status, error?.detail);
}

/**
 * FastAPI puts a string, an object ({code, message}) or, for validation errors, an array of
 * {loc, msg, type} in `detail`. A string keeps the plain message; the other shapes get a readable
 * message and the raw detail is kept on the error (`err.status`, `err.code`, `err.detail`).
 * @param {number} status
 * @param {any} detail
 */
function buildApiError(status, detail) {
  const fallback = `Request failed with status ${status}`;
  /** @type {Error & {status?: number, code?: string, detail?: unknown}} */
  let err;
  if (typeof detail === 'string' && detail) {
    err = new Error(detail);
  } else if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (!item || typeof item.msg !== 'string') return '';
        const field = Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : null;
        return typeof field === 'string' ? `${field}: ${item.msg}` : item.msg;
      })
      .filter(Boolean);
    err = new Error(parts.join('; ') || fallback);
    err.detail = detail;
  } else if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message) {
    err = new Error(detail.message);
    if (typeof detail.code === 'string') err.code = detail.code;
    err.detail = detail;
  } else {
    err = new Error(fallback);
  }
  err.status = status;
  return err;
}

const api = {
  async get(url) {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async post(url, data) {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: data != null ? JSON.stringify(data) : null,
    });
    return handleResponse(response);
  },

  async put(url, data) {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: data != null ? JSON.stringify(data) : null,
    });
    return handleResponse(response);
  },

  async patch(url, data) {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: data != null ? JSON.stringify(data) : null,
    });
    return handleResponse(response);
  },

  async del(url) {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /** GET a file (ZIP, CSV...) with the admin token; resolves to the Blob. */
  async download(url) {
    const response = await fetch(`${API_URL}${url}`, { method: 'GET', headers: getAuthHeaders() });
    if (response.ok) return response.blob();
    if (response.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
      return;
    }
    const error = await response.json().catch(() => ({ detail: 'Something went wrong' }));
    throw buildApiError(response.status, error?.detail);
  },

  async upload(url, formData) {
    const token = localStorage.getItem('adminToken');
    /** @type {Record<string, string>} */
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // No Content-Type — browser sets multipart boundary automatically
    const response = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return handleResponse(response);
  },
};

export default api;
